/**
 * Permission key constants + helpers for sidebar / route visibility.
 *
 * Every key here is seeded by Task 004 / 015 / 016 / 017 / 019 / 020 and
 * resolved through `me-context`. The matrix is UX-only per
 * `docs/frontend/035-frontend-app-shell-plan.md §5` — RLS is the
 * load-bearing security gate. If the matrix mis-categorises a role, the
 * endpoint will still filter correctly and the page will just render
 * empty, never leak data.
 */

import type { MeContext } from './api/types';

export const PERM = {
  STRATEGY_READ: 'strategy.read',
  // Task 292C3A — least-privilege strategy read for content_writer /
  // senior_content_writer. Backed by an additive RLS branch scoped to
  // (has_agency_role('content_writer' OR 'senior_content_writer')
  //  AND has_active_client_assignment(client_id)  -- ended_at IS NULL
  //                                                   AND clients.status='active'
  //  AND has_permission('strategy.read.assigned_active')).
  // Seeded ONLY for content_writer + senior_content_writer in
  // `20260905120000_writer_strategy_read_scope.sql`. Owner / MM /
  // account_manager keep their existing broader `strategy.read`
  // branch byte-for-byte.
  STRATEGY_READ_ASSIGNED_ACTIVE: 'strategy.read.assigned_active',
  STRATEGY_CREATE: 'strategy.create',
  STRATEGY_VERSION_PUBLISH: 'strategy.version.publish',
  // Task 247/251 — Strategy Rules review-surface gate. Seeded in
  // `20260720120000_strategy_rules_foundation.sql`; held by owner /
  // marketing_manager / media_buyer. Gates VIEWING the strategy-rules panel.
  // derive / approve / dismiss are additionally leadership-gated in the FE (and the
  // prod RPCs re-check), so media_buyer reviews/views but cannot act in V1.
  STRATEGY_RULE_REVIEW: 'strategy.rule.review',
  // Task 061: AI invocation gate (seeded in Task 056). Frontend
  // surfaces gate AI generation buttons on this so an unpermissioned
  // caller doesn't see an active button that 403s.
  AI_RUN: 'ai.run',
  CONTENT_READ: 'content.read',
  // Task 067: surfaces the AI calendar draft + manual add buttons on
  // the content page. Existing keys, already seeded; the constants
  // were missing here so the FE could not import them.
  CONTENT_CREATE: 'content.create',
  CONTENT_VARIANT_MANAGE: 'content.variant.manage',
  CONTENT_SCHEDULE: 'content.schedule',
  // Task 068 — review workflow gates. These keys are seeded in
  // `20260423040000_identity_tenant_access.sql` (lines 1016/1017/
  // 1031/1034/1036–1038) and are surfaced here so the calendar
  // workflow buttons can match the backend transition RPC's gates
  // exactly.
  PRODUCTION_REVIEW_ANY_OF: 'production.review.any_of',
  CONTENT_WRITING_APPROVE: 'content.writing.approve',
  CONTENT_DESIGN_APPROVE: 'content.design.approve',
  APPROVAL_CLIENT_DECIDE: 'approval.client.decide',
  // Task 101C — production-handoff CTA gate. Mirrors the backend
  // `transition_content_item_status_v2` v2 RPC's `client_content_approved
  // → in_production` branch, which requires
  // `production.queue.manage` on top of agency role +
  // (leadership OR active client_assignment). Seeded backend-side
  // since Task 004; surfaced here so the FE button matches the
  // server gate without a fragile string literal at the call site.
  PRODUCTION_QUEUE_MANAGE: 'production.queue.manage',
  // Task 101D — production-deliverable submit gate. Mirrors the
  // shared gate inside `start_production_task_v1`,
  // `prepare_production_task_upload_v1`, and
  // `submit_production_task_v1`. Held by designer / video_editor /
  // senior_designer / senior_content_writer / content_writer /
  // videographer (the actual production-execution roles). NOT held
  // by production_manager — managers coordinate, they don't deliver.
  PRODUCTION_ASSET_SUBMIT: 'production.asset.submit',
  // Task 101D Codex P2 (round 11) — discipline-specific execute
  // perms + the generic task.submit gate. The backend RPCs gate
  // self-claim/upload/submit on a discipline-matching
  // `task.{writing,design,video}.execute` perm (see
  // `app.require_task_discipline_perm` in the migration), and on
  // the generic `task.submit` perm (existing
  // `task_submissions_insert` RLS gate). The FE mirrors both so a
  // writer doesn't see Start/Submit on a design task — without it
  // those buttons would all 4xx.
  TASK_SUBMIT: 'task.submit',
  // Task 292C1 — the canonical "may assign a task" key; gates the writing handoff.
  TASK_ASSIGN: 'task.assign',
  TASK_WRITING_EXECUTE: 'task.writing.execute',
  TASK_DESIGN_EXECUTE: 'task.design.execute',
  TASK_VIDEO_EXECUTE: 'task.video.execute',
  PUBLISH_CONNECTION_READ: 'publish.connection.read',
  // Task 104C — publishing workflow V1 schedule + manual mark gate.
  // Seeded since Task 004 (`20260423040000_identity_tenant_access.sql`),
  // held by owner / marketing_manager / account_manager. Mirrors the
  // server-side gate in schedule_content_item_publish_v1,
  // mark_publish_job_published_v1, mark_publish_job_failed_v1, and
  // cancel_publish_job_v1.
  PUBLISH_SCHEDULE: 'publish.schedule',
  PAID_MEDIA_ACCOUNT_READ: 'paid_media.account.read',
  // Task 153 — agency manual Meta campaign-sync gate. Mirrors the
  // server-side gate in `sync-paid-media-meta-campaigns` (agency-category
  // role holding `paid_media.account.manage`: owner / marketing_manager
  // seeded, or a custom agency grant). Surfaced here so the "Sync
  // campaigns" button only renders for actors the EF would authorize.
  PAID_MEDIA_ACCOUNT_MANAGE: 'paid_media.account.manage',
  PAID_MEDIA_CAMPAIGN_READ: 'paid_media.campaign.read',
  CREATIVE_INTELLIGENCE_READ: 'creative.intelligence.read',
  INFLUENCER_CATALOG_READ: 'influencer.catalog.read',
  REPORT_READ: 'report.read',
  REPORT_FINANCE_READ: 'report.finance.read',
  CRM_LEAD_READ: 'crm.lead.read',
  CRM_DEAL_READ: 'crm.deal.read',
  TEAM_READ: 'team.read',
  TEAM_ROLE_ASSIGN: 'team.role.assign',
  TEAM_PERMISSION_MANAGE: 'team.permission.manage',
  INVITATION_READ: 'invitation.read',
  INVITATION_SEND: 'invitation.send',
  INVITATION_REVOKE: 'invitation.revoke',
  INVITATION_CLIENT_USER_MANAGE: 'invitation.client_user.manage',
  NOTIFICATION_READ: 'notification.read',
  CLIENT_READ: 'client.read',
  CLIENT_CREATE: 'client.create',
  CLIENT_MANAGE: 'client.manage',
  CLIENT_SELF_MANAGE: 'client.self.manage',
  INITIATIVE_CREATE: 'initiative.create',
  INITIATIVE_READ: 'initiative.read',
  // Agency commercial layer (Task 046). All keys pre-existing in
  // `supabase/migrations/20260423040000_identity_tenant_access.sql`.
  BILLING_READ: 'billing.read',
  BILLING_SUBSCRIPTION_MANAGE: 'billing.subscription.manage',
  CONTRACT_READ: 'contract.read',
  // Task 303-A — the agency-client contract write gate. Key already
  // seeded (`20260423040000:997`) and held by owner / marketing_manager
  // ONLY, which is exactly the write authority 303-A §2.8 locks.
  // `contract.send` is deliberately NOT surfaced here: account_manager
  // holds it and that role is read-only for contracts.
  CONTRACT_CREATE: 'contract.create',
  // Task 303-B — the client acknowledgement gate. A NEW catalogue key
  // (295A §6.2), seeded to `client_admin` and to nothing else.
  //
  // `contract.sign` was the other candidate and is wrong twice over: it is
  // signature-shaped by name and description, on a surface whose whole
  // point is that no signature happens in this product, and 303-A §2.3
  // records that removing it is part of a planned legacy retirement — so
  // gating on it would make that retirement break this feature.
  //
  // It is NOT used to gate a route or a button here. The acknowledgement
  // block renders from the server's own `can_acknowledge`, and this
  // constant exists so the key has one spelling in the frontend.
  CONTRACT_ACKNOWLEDGE: 'contract.acknowledge',
  // Task 071 — agency knowledge library (prompt blocks). Seeded in
  // `supabase/migrations/20260423040000_identity_tenant_access.sql`;
  // surfaced here so the sidebar entry + page guard can match the
  // backend RLS gate exactly.
  AI_KNOWLEDGE_PACK_MANAGE: 'ai.knowledge_pack.manage',
  // Task 073 — creative asset upload + attach. Both keys pre-existing
  // (Task 019); surfaced here so the upload button matches the
  // backend RLS gate (uploader needs BOTH manage perms — atomic
  // upload-and-attach).
  CREATIVE_INTELLIGENCE_ASSET_MANAGE: 'creative.intelligence.asset.manage',
  CREATIVE_INTELLIGENCE_MATCH_MANAGE: 'creative.intelligence.match.manage',
} as const;

/**
 * V1 capability keys — must stay in sync with the CHECK constraint in
 * `supabase/migrations/20260427130000_agency_services_packages.sql`
 * and with the runtime allow-list in
 * `supabase/functions/upsert-agency-service/index.ts`.
 */
export const SYSTEM_CAPABILITY_KEYS = [
  'strategy',
  'content',
  'publishing',
  'paid_media',
  'creative',
  'influencers',
  'reports',
  'crm',
  'calendar',
  'client_onboarding',
  'custom',
] as const;
export type SystemCapabilityKey = (typeof SYSTEM_CAPABILITY_KEYS)[number];

/**
 * Arabic display label for a capability key. Used by the services
 * catalog and client assignment surfaces. The wire surface keeps the
 * raw enum value; this helper maps it for display only.
 */
export function capabilityLabelAr(key: SystemCapabilityKey): string {
  switch (key) {
    case 'strategy':
      return 'الاستراتيجية';
    case 'content':
      return 'المحتوى';
    case 'publishing':
      return 'النشر';
    case 'paid_media':
      return 'الإعلانات المدفوعة';
    case 'creative':
      return 'مكتبة الإبداع';
    case 'influencers':
      return 'المؤثرون';
    case 'reports':
      return 'التقارير';
    case 'crm':
      return 'علاقات العملاء';
    case 'calendar':
      return 'التقويم';
    case 'client_onboarding':
      return 'تأهيل العملاء';
    case 'custom':
      return 'خدمة مخصّصة';
    default:
      return key;
  }
}

export const LEADERSHIP_ROLE_KEYS: ReadonlyArray<string> = ['owner', 'marketing_manager'];

export function hasAnyPermission(
  permissions: ReadonlyArray<string>,
  ...keys: ReadonlyArray<string>
): boolean {
  for (const k of keys) {
    if (permissions.includes(k)) return true;
  }
  return false;
}

export function isLeadership(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && LEADERSHIP_ROLE_KEYS.includes(r.key)) return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `owner` role. Display-only, like
 * `isLeadership`. `owner` is a strict subset of leadership: every owner is
 * leadership, but `marketing_manager` (also leadership) is NOT an owner.
 *
 * Task 204 gates the Owner Command Center on this (owner-only). The
 * `marketing_manager` lands on the existing agency-wide home until its own
 * dedicated Marketing Manager Command Center ships in a follow-up PR.
 */
export function isOwner(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'owner') return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `marketing_manager` role. Display-only,
 * like `isOwner`. Also a subset of `isLeadership`. Task 205 gates the
 * Marketing Manager Command Center on this (marketing_manager-only). The
 * `owner` keeps the Owner Command Center (its gate runs first in AppLanding),
 * so an actor who somehow holds both roles resolves to the owner surface.
 */
export function isMarketingManager(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'marketing_manager') return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `account_manager` role. Display-only,
 * like `isOwner` / `isMarketingManager`. Task 206 gates the Account Manager
 * Command Center on this AND a multi-client check (`client_assignments.length
 * > 1`): a single-client account_manager keeps the existing single-client
 * bounce, and a zero-client account_manager keeps the Task 200 scoped empty
 * state. The owner / marketing_manager gates run first in AppLanding, so an
 * actor who also holds one of those resolves to that higher surface.
 */
export function isAccountManager(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'account_manager') return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `media_buyer` role. Display-only, like
 * the other command-center helpers. `media_buyer` is a SPECIALIST (it is in
 * `SPECIALIST_ROLE_KEYS`), so it normally lands on the Task 200 scoped home;
 * Task 207 carves a MULTI-client media_buyer out to a paid-media command
 * center via this helper + a `client_assignments.length > 1` check, placed
 * after the owner / marketing_manager / account_manager gates. A single-client
 * media_buyer keeps the existing single-client bounce, and a zero-client one
 * keeps the Task 200 scoped empty state. media_buyer holds only paid-media
 * READ permissions — the surface stays read-only (no write/sync controls).
 */
export function isMediaBuyer(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'media_buyer') return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `designer` role. Display-only, like the
 * other role helpers.
 *
 * Routing note (Task 208): the Designer Workspace is the `designer` ROLE HOME.
 * `designer` is a PRODUCTION-execute role — it holds `production.asset.submit`,
 * so `isAgencyProductionOnly` is true for it and the production redirect (→
 * /app/me/tasks) would otherwise fire at the FIRST gate. The DesignerWorkspace
 * gate is placed BEFORE that production redirect and intentionally CARVES
 * designers out of it: ANY designer (zero-, single-, or multi-client) lands on
 * the workspace (a zero-client designer sees the workspace empty state, not
 * /app/me/tasks). The gate is guarded against the higher command-center roles
 * (owner / marketing_manager / account_manager / media_buyer), which keep
 * precedence; every OTHER production-only role still hits the production
 * redirect.
 */
export function isDesigner(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'designer') return true;
  }
  return false;
}

/**
 * True iff the actor holds an agency VIDEO-PRODUCTION role — `video_editor`
 * (مونتير) or `videographer` (مصوّر). Display-only, like `isDesigner`.
 *
 * Routing note (Task 217): the Video Producer Workspace is the role HOME for
 * these two roles (there is NO `video_producer` role in this system — these are
 * the real seeded video roles, and both already hold `production.asset.submit`
 * + `task.submit` + `task.video.execute`). Both are PRODUCTION-execute roles,
 * so `isAgencyProductionOnly` is true for them and the production redirect (→
 * /app/me/tasks) would otherwise fire. The VideoProducerWorkspace gate is placed
 * BEFORE that redirect (and AFTER the designer gate) and CARVES video producers
 * out of it: ANY video producer (zero-, single-, or multi-client) lands on the
 * workspace; a zero-client one sees its empty state. Guarded against the higher
 * command-center roles (owner / marketing_manager / account_manager /
 * media_buyer) AND `designer`, which all keep precedence; every OTHER
 * production-only role still hits the production redirect. RLS stays the
 * load-bearing gate (UI-only helper, same posture as `isDesigner`).
 */
export function isVideoProducer(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && (r.key === 'video_editor' || r.key === 'videographer')) {
      return true;
    }
  }
  return false;
}

/**
 * True iff the actor holds the agency `content_writer` role. Display-only.
 *
 * Routing note (Task 219, backed by Task 220): the Content Writer Workspace is
 * the role HOME for `content_writer` — a PRODUCTION-execute role (holds
 * `production.asset.submit` + `task.writing.execute`) whose deliverable is
 * IN-SYSTEM TEXT (via the Task 220 content-writer-draft RPCs/EFs), not a file
 * upload. Its gate runs BEFORE the production redirect and AFTER the senior
 * gate, guarded against the higher command-center roles + designer + video
 * producer. RLS stays the load-bearing gate.
 */
export function isContentWriter(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'content_writer') return true;
  }
  return false;
}

/**
 * True iff the actor holds the agency `senior_content_writer` role. Display-only.
 *
 * Routing note (Task 219): the Senior Content Writer Workspace is the review
 * home for `senior_content_writer` (holds `content.writing.approve` +
 * `approval.internal.decide` + `approval.override.content`). Its gate runs
 * FIRST among the content roles, so a `content_writer` + `senior_content_writer`
 * dual-hat resolves to the SENIOR review surface. Guarded against the higher
 * command-center roles + designer + video producer.
 */
export function isSeniorContentWriter(me: MeContext): boolean {
  for (const r of me.roles) {
    if (r.category === 'agency' && r.key === 'senior_content_writer') return true;
  }
  return false;
}

export function hasRoleCategory(
  me: MeContext,
  category: 'agency' | 'client' | 'influencer',
): boolean {
  for (const r of me.roles) {
    if (r.category === category) return true;
  }
  return false;
}

/**
 * Task 118 — True iff the actor is agency production-execute staff
 * (designer / senior_designer / video_editor / videographer /
 * content_writer / senior_content_writer) and NOT leadership and
 * NOT a sysadmin.
 *
 * Drives the focused Employee Workspace nav: `Sidebar` collapses the
 * agency-admin nav (CRM, reports, paid media, services, team management,
 * knowledge library) to a focused 5-item rail (الرئيسية / مهامي, العملاء,
 * مكتبة الإبداع, الإشعارات, الإعدادات) for ALL of these roles. `AppLanding`
 * redirects them to `/app/me/tasks` EXCEPT the roles carved out to a
 * dedicated role home that runs BEFORE the redirect: `designer` →
 * DesignerWorkspace (Task 208) and `video_editor` / `videographer` →
 * VideoProducerWorkspace (Task 217). So the redirect now catches the
 * REMAINING production-execute roles (senior_designer / content_writer /
 * senior_content_writer).
 *
 * Detection uses `PRODUCTION_ASSET_SUBMIT` — the perm held by every
 * production-execute role (mirrors the backend gate inside
 * `start_production_task_v1` / `prepare_production_task_upload_v1` /
 * `submit_production_task_v1`). Production_manager does NOT hold it
 * (they coordinate; they don't deliver), so they stay on the
 * leadership-style landing.
 *
 * Same UI-only posture as `isClientOnly` / `isLeadership`: RLS is the
 * load-bearing security gate, this helper just shapes the nav.
 */
export function isAgencyProductionOnly(me: MeContext): boolean {
  if (me.system_admin) return false;
  if (isLeadership(me)) return false;
  if (!hasRoleCategory(me, 'agency')) return false;
  return hasAnyPermission(me.permissions, PERM.PRODUCTION_ASSET_SUBMIT);
}

/**
 * True iff the actor carries only client-tier role(s) — no agency seat
 * and not a sysadmin. Drives client-portal UX in the shell + team page:
 * hides the "الوكالة" sidebar group, retitles the team route to "فريق
 * العميل", and pins the team page's scope filter to the actor's
 * assigned client so the client-scoped readers in `list-user-roles` /
 * `list-user-invitations` never receive `client_id=undefined` (which
 * `list-user-roles` rejects with
 * `validation_error: required_for_client_scoped_reader` because it
 * requires `team.read` for cross-org reads).
 */
export function isClientOnly(me: MeContext): boolean {
  if (me.system_admin) return false;
  if (hasRoleCategory(me, 'agency')) return false;
  return hasRoleCategory(me, 'client');
}

export function hasClientAssignment(me: MeContext, clientId: string): boolean {
  for (const a of me.client_assignments) {
    if (a.client_id === clientId) return true;
  }
  return false;
}

/**
 * Task 292C3A — display-only helper. TRUE iff the actor has a
 * `client_assignments` row for `clientId` with `client_status === 'active'`.
 * The backend (`app.has_active_client_assignment`) is the load-bearing
 * gate; this mirrors it so the writer's sidebar link and the
 * ClientWorkspaceLayout Strategy tab do not render for a client whose
 * status collapsed to `onboarding` / `paused` / `archived` between
 * a page mount and the next `me-context` refresh.
 */
export function hasActiveClientAssignment(me: MeContext, clientId: string): boolean {
  for (const a of me.client_assignments) {
    if (a.client_id === clientId && a.client_status === 'active') return true;
  }
  return false;
}

/**
 * Whether the actor can navigate to a given client workspace. Leadership
 * is admitted org-wide; everyone else needs an active client assignment.
 * Sysadmins are excluded — their tenant routes collapse to 404.
 */
export function canSeeClient(me: MeContext, clientId: string): boolean {
  if (me.system_admin) return false;
  if (isLeadership(me)) return true;
  return hasClientAssignment(me, clientId);
}

// ─────────────────────────────────────────────────────────────────────
// Task 200 — role-aware staff home (display-only helpers)
// ─────────────────────────────────────────────────────────────────────
// `me.roles` is a RoleSummary (`{ key, category }`) and does NOT carry the
// seeded `name_ar`, so the FE keeps its own Arabic display map mirroring
// `roles.name_ar` from `20260423040000_identity_tenant_access.sql §7.1`.
// Everything below shapes the landing surface + sidebar labels ONLY; it
// never gates data — RLS is the load-bearing gate (same UI-only posture as
// `isLeadership` / `isAgencyProductionOnly`).

/** Arabic display labels for role keys. Mirrors the seeded `roles.name_ar`. */
export const ROLE_LABELS_AR: Readonly<Record<string, string>> = {
  system_admin: 'مدير النظام',
  owner: 'مالك الوكالة',
  marketing_manager: 'مدير التسويق',
  department_manager: 'مدير قسم',
  account_manager: 'مدير حسابات',
  senior_designer: 'كبير المصممين',
  designer: 'مصمم',
  senior_content_writer: 'كبير كتّاب المحتوى',
  content_writer: 'كاتب محتوى',
  production_manager: 'مدير الإنتاج',
  video_editor: 'مونتير',
  videographer: 'مصوّر',
  media_buyer: 'مسؤول الإعلانات المدفوعة',
  finance_manager: 'مدير مالي',
  client_admin: 'مدير العميل',
  client_manager: 'منسق العميل',
  client_approver: 'معتمد العميل',
  client_viewer: 'مشاهد العميل',
  influencer: 'مؤثر',
};

/** Arabic display label for a role key; falls back to the raw key. */
export function roleLabelAr(key: string): string {
  return ROLE_LABELS_AR[key] ?? key;
}

/**
 * Agency role-key precedence for picking the actor's "primary hat" when
 * they hold several. Display-only (headline label + home shaping).
 */
const AGENCY_ROLE_PRECEDENCE: ReadonlyArray<string> = [
  'owner',
  'marketing_manager',
  'finance_manager',
  'department_manager',
  'production_manager',
  'account_manager',
  'media_buyer',
  'senior_designer',
  'senior_content_writer',
  'designer',
  'content_writer',
  'video_editor',
  'videographer',
];

/** Highest-precedence AGENCY role key the actor holds, or null. */
export function primaryAgencyRole(me: MeContext): string | null {
  const held = new Set(me.roles.filter((r) => r.category === 'agency').map((r) => r.key));
  for (const key of AGENCY_ROLE_PRECEDENCE) {
    if (held.has(key)) return key;
  }
  return null;
}

/**
 * Role keys whose work is scoped to assigned clients/tasks within a single
 * specialty (paid media / design / video / content). They get an
 * "assigned-clients-first" home rather than the agency-wide browse.
 */
const SPECIALIST_ROLE_KEYS: ReadonlyArray<string> = [
  'media_buyer',
  'designer',
  'senior_designer',
  'video_editor',
  'videographer',
  'content_writer',
  'senior_content_writer',
];

/**
 * UX home grouping for agency staff (Task 200). Display-only.
 *   - 'leadership'      → owner / marketing_manager (agency-wide home).
 *   - 'account_manager' → assigned-client delivery owner.
 *   - 'specialist'      → media_buyer / design / video / content roles.
 *   - 'agency_other'    → any other agency role (e.g. department_manager).
 * Returns null for sysadmin and non-agency (client / influencer) actors,
 * which have their own dedicated surfaces.
 */
export type StaffHomeGroup = 'leadership' | 'account_manager' | 'specialist' | 'agency_other';

export function staffHomeGroup(me: MeContext): StaffHomeGroup | null {
  if (me.system_admin) return null;
  if (!hasRoleCategory(me, 'agency')) return null;
  if (isLeadership(me)) return 'leadership';
  // Derive the group from the SAME precedence the headline uses
  // (`primaryAgencyRole`) so a multi-hat user's home structure and
  // headline never disagree — e.g. finance_manager + account_manager
  // resolves to finance_manager (agency-wide home), not the
  // assigned-clients-scoped home.
  const primary = primaryAgencyRole(me);
  if (primary === 'account_manager') return 'account_manager';
  if (primary !== null && SPECIALIST_ROLE_KEYS.includes(primary)) return 'specialist';
  return 'agency_other';
}

/**
 * Whether the actor's home should lead with their ASSIGNED clients
 * (account_manager + specialists) instead of the agency-wide browse.
 */
export function isAssignedClientScopedHome(me: MeContext): boolean {
  const group = staffHomeGroup(me);
  return group === 'account_manager' || group === 'specialist';
}
